import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import { bankAccountGetBankAccount } from '@column/api';
import { Button, Input, MoneyInput } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Loading, RenderFields } from '~/components';
import { SearchSelect, type SearchSelectOption } from '~/components/SearchSelect';
import { useGetPlatformIntlWireConfig } from '~/hooks';
import { useAccountNumber, useBankAccount } from '~/hooks/useBankAccounts';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { BankAccountSelector } from '~/lib/components/selectors';
import {
  type ApprovalAmounts,
  type ApprovalConditions,
  type ConfigurableApprovalRuleKey,
  buildApprovalRules,
  findApprovalRule,
} from '~/lib/transfers';
import { type PlatformLevelType } from '~/models';
import { EntityRepository, EntityWithDetails } from '~/repositories/EntityRepository';
import {
  InternationalWireBillingConfig,
  PlatformRepository,
  type TransferApprovalRules,
} from '~/repositories/PlatformRepository';
import { isStepUpRequiredError } from '~/repositories/StepUpRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';
import { formatDateTime } from '~/util';

import { ApprovalRules } from './_components/ApprovalRules';
import { PlatformLevelChip } from './_components/PlatformLevelChip';
import { RootEntityField } from './_components/RootEntityField';
import { SSOConnection } from './_components/SSOConnection';
import { useSaveUserChangeApproval } from './_hooks/useSaveUserChangeApproval';

interface Params {
  id?: string;
}

export interface PlatformSettingsFields {
  // Root Entity
  rootEntity?: never;

  // Platform Settings
  createdAt?: Date;
  id: string;
  name: string;
  platformLevel: string;
  reportingTimeZone?: string;
  requireUserChangeApproval?: boolean;

  // International Wire Config
  revenueAccountNumberId?: string;
  fixedFee?: number;
  fxRateMarginBps?: number;

  // Transfer Approval Config
  enabledForAchCredits?: boolean;
  enabledForAchDebits?: boolean;
  enabledForCheckDebits?: boolean;
  enabledForInternationalWire?: boolean;
  enabledForWire?: boolean;
  enabledForRealtime?: boolean;
  approvalAmounts?: ApprovalAmounts;
  approvalConditions?: ApprovalConditions;
  approvalRules?: TransferApprovalRules;
}

// International wire fees are billed in USD.
const FEE_CURRENCY = 'USD';

export const PagePlatformSettings: React.FC = () => {
  const { currentUser, currentPlatform, loadPlatform, isSandbox } = useSessionStore();
  const location = useLocation();
  const { id } = useParams<keyof Params>() as Params;
  const platformId = useMemo(() => {
    if (location.pathname === ROUTE.PLATFORM_SETTINGS && currentUser?.defaultPlatformId) {
      return currentUser.defaultPlatformId;
    }
    return id;
  }, [currentPlatform, currentUser, id]);

  const transferApprovalConfigLocked = currentPlatform?.transferApprovalConfig?.locked === true;
  const canConfigureTransferApprovals = !transferApprovalConfigLocked;

  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler();
  const {
    response: revenueBankAccountResponse,
    isLoading: isRevenueBankAccountLoading,
    createRequest: getRevenueBankAccount,
  } = useBankAccount();
  const { createRequest: getRevenueAccountNumber } = useAccountNumber({
    onSuccess: (d) => getRevenueBankAccount({ id: d.bankAccountId, platformId }),
  });
  const [intlWireConfig, setIntlWireConfig] = useState<InternationalWireBillingConfig>();
  const { createRequest: getPlatformIntlWireConfig, isLoading: isIntlWireConfigLoading } = useGetPlatformIntlWireConfig(
    {
      onSuccess: (data) => {
        setIntlWireConfig(data);
        if (data.revenueAccountNumberId) {
          getRevenueAccountNumber({ id: data.revenueAccountNumberId, platformId });
        }
      },
      onError: (error) => {
        addDangerNotification({
          content: error.message ?? 'Something went wrong getting International Wire Billing Config, please try again.',
        });
      },
    }
  );

  const [timezones, setTimezones] = useState<SearchSelectOption[]>([]);

  const {
    data: rootEntity,
    isLoading: isRootEntityLoading,
    error: rootEntityError,
  } = useQuery<EntityWithDetails | null>({
    queryKey: [currentPlatform?.id, 'platform', 'root-entity', isSandbox],
    queryFn: () => EntityRepository.getRoot(currentPlatform?.id ?? '', isSandbox ? 'sandbox' : 'live'),
    enabled: !!currentPlatform?.id,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as Partial<ApiError> | undefined;
      if (apiError?.code === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });
  const rootEntityNotFound =
    rootEntityError && (rootEntityError as Partial<ApiError>).code === ApiErrCode.ResourceNotFound.toString();

  const formMethods = useForm<PlatformSettingsFields>({
    defaultValues: {
      createdAt: currentPlatform?.createdAt,
      name: currentPlatform?.name,
      platformLevel: currentPlatform?.platformLevel,
      reportingTimeZone: currentPlatform?.reportingTimeZone,
      requireUserChangeApproval: currentPlatform?.requireUserChangeApproval ?? false,
      enabledForCheckDebits: currentPlatform?.transferApprovalConfig?.enabledForCheckDebits ?? false,
      enabledForInternationalWire: currentPlatform?.transferApprovalConfig?.enabledForInternationalWire ?? false,
      enabledForWire: currentPlatform?.transferApprovalConfig?.enabledForWire ?? false,
    },
  });
  const { handleSubmit, setValue } = formMethods;
  const saveUserChangeApproval = useSaveUserChangeApproval((value) => setValue('requireUserChangeApproval', value));
  const resetTransferApprovalFields = useCallback(() => {
    const transferApprovalConfig = currentPlatform?.transferApprovalConfig;
    const rules = transferApprovalConfig?.rules ?? [];
    const approvalEntries: {
      enabled: boolean;
      key: ConfigurableApprovalRuleKey;
    }[] = [
      { key: 'achCredits', enabled: transferApprovalConfig?.enabledForAchCredits ?? false },
      { key: 'achDebits', enabled: transferApprovalConfig?.enabledForAchDebits ?? false },
      { key: 'checkDebits', enabled: transferApprovalConfig?.enabledForCheckDebits ?? false },
      { key: 'internationalWire', enabled: transferApprovalConfig?.enabledForInternationalWire ?? false },
      { key: 'wire', enabled: transferApprovalConfig?.enabledForWire ?? false },
      { key: 'realtime', enabled: transferApprovalConfig?.enabledForRealtime ?? false },
    ];

    setValue('enabledForAchCredits', currentPlatform?.transferApprovalConfig?.enabledForAchCredits ?? false);
    setValue('enabledForAchDebits', currentPlatform?.transferApprovalConfig?.enabledForAchDebits ?? false);
    setValue('enabledForCheckDebits', currentPlatform?.transferApprovalConfig?.enabledForCheckDebits ?? false);
    setValue(
      'enabledForInternationalWire',
      currentPlatform?.transferApprovalConfig?.enabledForInternationalWire ?? false
    );
    setValue('enabledForWire', currentPlatform?.transferApprovalConfig?.enabledForWire ?? false);
    setValue('enabledForRealtime', currentPlatform?.transferApprovalConfig?.enabledForRealtime ?? false);
    setValue('approvalRules', rules);
    setValue(
      'approvalConditions',
      Object.fromEntries(
        approvalEntries.map(({ enabled, key }) => {
          const rule = findApprovalRule(rules, key);
          return [key, !enabled ? 'disabled' : rule?.condition.amountGte !== undefined ? 'threshold' : 'all'];
        })
      )
    );
    setValue(
      'approvalAmounts',
      Object.fromEntries(
        approvalEntries.map(({ key }) => {
          const amountGte = findApprovalRule(rules, key)?.condition.amountGte;
          return [key, amountGte];
        })
      )
    );
  }, [currentPlatform, setValue]);
  useEffect(() => {
    if (!currentPlatform) return;
    setValue('createdAt', currentPlatform.createdAt);
    setValue('id', currentPlatform.id);
    setValue('name', currentPlatform.name ?? '');
    setValue('platformLevel', currentPlatform.platformLevel ?? '');
    setValue('reportingTimeZone', currentPlatform.reportingTimeZone ?? '');
    setValue('requireUserChangeApproval', currentPlatform.requireUserChangeApproval ?? false);
    resetTransferApprovalFields();
  }, [currentPlatform, resetTransferApprovalFields, setValue]);
  useEffect(() => {
    if (!intlWireConfig) return;
    setValue('fixedFee', intlWireConfig.fixedFee);
    setValue('fxRateMarginBps', intlWireConfig.fxRateMarginBps);
  }, [intlWireConfig]);
  useEffect(() => {
    if (!revenueBankAccountResponse) return;
    setValue('revenueAccountNumberId', revenueBankAccountResponse?.id);
  }, [revenueBankAccountResponse]);

  const handleSave = useCallback(
    async (data: PlatformSettingsFields) => {
      if (!platformId || !currentUser) {
        return;
      }

      const remainingUpdates: (() => Promise<unknown>)[] = [
        () =>
          PlatformRepository.update({
            platformId,
            name: data.name,
            reportingTimeZone: data.reportingTimeZone,
          }),
      ];

      if (data.revenueAccountNumberId || data.fixedFee || data.fxRateMarginBps) {
        if (!data.revenueAccountNumberId) {
          addDangerNotification({
            content: 'Please select a valid revenue account number',
          });
          return;
        }
        const { data: revenueBankAccount } = await bankAccountGetBankAccount({
          path: { bank_account_id: data.revenueAccountNumberId },
          throwOnError: true,
        });
        const revenueAccountNumberId = revenueBankAccount?.default_account_number_id;
        if (!revenueAccountNumberId) {
          addDangerNotification({
            content: 'Please select a valid revenue account number',
          });
          return;
        }
        remainingUpdates.push(() =>
          PlatformRepository.updateInternationalWireConfig({
            revenueAccountNumberId,
            fixedFee: data.fixedFee,
            fxRateMarginBps: data.fxRateMarginBps,
            platformId,
          })
        );
      }

      try {
        if (canConfigureTransferApprovals) {
          const approvalEnabled = (key: ConfigurableApprovalRuleKey, fallback?: boolean) =>
            data.approvalConditions?.[key] ? data.approvalConditions[key] !== 'disabled' : fallback;
          await PlatformRepository.updateConfig({
            transferApprovalConfig: {
              enabledForAchCredits: approvalEnabled('achCredits', data.enabledForAchCredits),
              enabledForAchDebits: approvalEnabled('achDebits', data.enabledForAchDebits),
              enabledForCheckDebits: approvalEnabled('checkDebits', data.enabledForCheckDebits),
              enabledForWire: approvalEnabled('wire', data.enabledForWire),
              rules: buildApprovalRules({
                amounts: data.approvalAmounts,
                conditions: data.approvalConditions,
                existingRules: data.approvalRules,
              }),
              enabledForRealtime: approvalEnabled('realtime', data.enabledForRealtime),
              enabledForInternationalWire: approvalEnabled('internationalWire', data.enabledForInternationalWire),
            },
            platformId,
          });
        }

        await Promise.all(remainingUpdates.map((update) => update()));
        const { pendingApproval } = await saveUserChangeApproval.mutateAsync({
          platformId,
          value: data.requireUserChangeApproval,
        });
        addSuccessNotification({
          content: pendingApproval ? 'Change submitted for approval' : 'Platform saved',
        });
        await loadPlatform({ fetchMeta: true });
      } catch (error) {
        if (isStepUpRequiredError(error)) {
          resetTransferApprovalFields();
        }
        addDangerNotification({
          content:
            (typeof error === 'object' && error !== null && (error as { message?: string }).message) ||
            'failed to save platform updates',
        });
      }
    },
    [
      addSuccessNotification,
      addDangerNotification,
      currentUser,
      canConfigureTransferApprovals,
      loadPlatform,
      platformId,
      resetTransferApprovalFields,
      saveUserChangeApproval.mutateAsync,
    ]
  );

  const fetchInit = () => {
    if (currentUser && platformId) {
      getPlatformIntlWireConfig({ platformId });
    }
  };
  useEffect(fetchInit, [platformId]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        () => fetchInit(),
        {
          fireImmediately: true,
        }
      ),
    []
  );

  useEffect(() => {
    import('~/data/timezones.json').then((json) => {
      setTimezones(
        Object.entries(json.default).map(([timezone, description]) => ({
          label: timezone,
          description,
          value: timezone,
        }))
      );
    });
  }, []);

  return (
    <>
      <Loading
        isLoading={
          (!intlWireConfig && isIntlWireConfigLoading && !revenueBankAccountResponse && isRevenueBankAccountLoading) ||
          !currentPlatform
        }
      >
        <FormProvider {...formMethods}>
          <form id="platform-settings-form" autoComplete="off" onSubmit={handleSubmit(handleSave, handleSubmitErrors)}>
            {RenderFields<PlatformSettingsFields>({
              sections: [
                {
                  headline: 'General',
                  fields: [
                    {
                      id: 'rootEntity',
                      label: 'Root Entity',
                      children: () => (
                        <RootEntityField
                          entity={rootEntity}
                          isLoading={isRootEntityLoading}
                          isError={!!rootEntityError}
                          isNotFound={!!rootEntityNotFound}
                        />
                      ),
                    },
                    {
                      id: 'name',
                      label: 'Name',
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { error }) => (
                        <Input
                          ref={ref}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          placeholder="Name"
                          aria-invalid={!!error}
                        />
                      ),
                    },
                    {
                      id: 'reportingTimeZone',
                      label: 'Reporting Time Zone',
                      children: ({ value, onChange, onBlur }) => (
                        <SearchSelect
                          value={value ? String(value) : undefined}
                          onChange={onChange}
                          onOpenChange={(open) => !open && onBlur()}
                          options={timezones}
                          placeholder="Select a time zone"
                          searchPlaceholder="Search time zones…"
                          pinSelected
                        />
                      ),
                    },
                    {
                      id: 'id',
                      label: 'ID',
                      children: ({ value }) => (
                        <Input
                          variant="copy"
                          readOnly
                          showCopyButton
                          className="[&_input]:font-mono"
                          value={String(value ?? '')}
                        />
                      ),
                    },
                    {
                      id: 'createdAt',
                      label: 'Created',
                      children: ({ value }) => (
                        <Input
                          variant="copy"
                          readOnly
                          showCopyButton
                          value={value ? formatDateTime(value as Date, { timeZoneFormat: 'utc' }) : '-'}
                        />
                      ),
                    },
                    {
                      id: 'platformLevel',
                      label: 'Platform Level',
                      // Centered on the label (12px down, 18px line): the 30px chip starts 6px down.
                      children: ({ value }) =>
                        value ? (
                          <PlatformLevelChip level={value as PlatformLevelType} className="@tablet/form-field:mt-1.5" />
                        ) : null,
                    },
                  ],
                },
              ],
            })}

            <SSOConnection />

            {RenderFields<PlatformSettingsFields>({
              sections: [
                {
                  headline: 'International Wire Billing Configuration',
                  fields: [
                    {
                      id: 'revenueAccountNumberId',
                      label: 'Revenue Account Number',
                      tooltip: 'Account number ID for international wire revenues of a platform',
                      children: ({ value, onChange, onBlur }) => (
                        <BankAccountSelector
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          dataCy="revenue-account"
                        />
                      ),
                    },
                    {
                      id: 'fixedFee',
                      label: 'Fixed Fee',
                      children: ({ value, onChange, onBlur }) => (
                        <MoneyInput
                          currency={FEE_CURRENCY}
                          value={typeof value === 'number' ? value : undefined}
                          onValueChange={onChange}
                          onBlur={onBlur}
                        />
                      ),
                    },
                    {
                      id: 'fxRateMarginBps',
                      label: 'FX Rate Margin BPS',
                      tooltip: 'Number of basis points (BPS) for FX margin. 1 bps = 0.01%',
                      children: ({ value, onChange, onBlur, ref }) => (
                        <Input
                          ref={ref}
                          inputMode="numeric"
                          value={value ? String(value) : ''}
                          // Whole basis points only, like the API.
                          onChange={(event) => onChange(Number(event.target.value.replace(/\D/g, '')))}
                          onBlur={onBlur}
                          placeholder="FX Rate Margin BPS"
                        />
                      ),
                    },
                  ],
                },
              ],
            })}

            <ApprovalRules canConfigureTransferApprovals={canConfigureTransferApprovals} />

            <div className="flex justify-end px-(--page-margin) py-6">
              <Button type="submit" size="sm" disabled={formMethods.formState.isSubmitting}>
                {formMethods.formState.isSubmitting ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </FormProvider>
      </Loading>
    </>
  );
};
